import React, { useState } from "react";

/**
 * SpintaxParser Component
 * Renders a string and converts any spintax patterns {opt1|opt2} into interactive chips.
 * Supports editing variants via a popover, similar to quillEditor.js.
 */
const SpintaxParser = ({ content = "", onChange = () => {}, onEditAt = null }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [showWorkingDaysPopover, setShowWorkingDaysPopover] = useState(false);
  const [workingDaysCount, setWorkingDaysCount] = useState(1);
  const [workingDaysType, setWorkingDaysType] = useState("working");
  const [workingDaysPopoverPosition, setWorkingDaysPopoverPosition] = useState({ top: 0, left: 0 });
  const [workingDaysEditingIndex, setWorkingDaysEditingIndex] = useState(null);

  // Splits a spintax content string by | separators that are NOT inside {{ }} variable tokens
  const splitByPipeOutsideVars = (str) => {
    const parts = [];
    let current = "";
    let i = 0;
    while (i < str.length) {
      if (str[i] === "{" && str[i + 1] === "{") {
        const close = str.indexOf("}}", i + 2);
        if (close !== -1) {
          current += str.substring(i, close + 2);
          i = close + 2;
          continue;
        }
      } else if (str[i] === "{" && str[i + 1] !== "{") {
        // Balanced skip for nested spintax or braced blocks
        let depth = 1;
        let j = i + 1;
        while (j < str.length && depth > 0) {
          if (str[j] === "{" && str[j + 1] === "{") {
            const close = str.indexOf("}}", j + 2);
            if (close !== -1) j = close + 1;
          } else if (str[j] === "{") depth++;
          else if (str[j] === "}") depth--;
          j++;
        }
        current += str.substring(i, j);
        i = j;
        continue;
      }
      
      if (str[i] === "|") {
        parts.push(current);
        current = "";
        i++;
        continue;
      }
      current += str[i];
      i++;
    }
    parts.push(current);
    return parts;
  };

  const workingDaysRegex = /^(\d+|x) (working days|days) from today$/i;

  const parseTextSegment = (text) => {
    const segments = [];
    const varRegex = /\{\{([^{}]+)\}\}/g;
    let last = 0;
    let match;
    while ((match = varRegex.exec(text)) !== null) {
      if (match.index > last) {
        segments.push({ type: "text", value: text.slice(last, match.index) });
      }
      const varType = workingDaysRegex.test(match[1].trim()) ? "workingDays" : "variable";
      segments.push({ type: varType, value: match[1], raw: match[0] });
      last = match.index + match[0].length;
    }
    if (last < text.length) {
      segments.push({ type: "text", value: text.slice(last) });
    }
    return segments;
  };

  const parseContent = (text) => {
    if (!text) return [];
    const parts = [];
    let lastIndex = 0;
    let i = 0;

    while (i < text.length) {
      if (text[i] === "{" && text[i + 1] !== "{") {
        let depth = 1;
        let j = i + 1;
        let hasPipeAtLevel1 = false;
        while (j < text.length && depth > 0) {
          if (text[j] === "{" && text[j + 1] === "{") {
            const close = text.indexOf("}}", j + 2);
            if (close !== -1) j = close + 1;
          } else if (text[j] === "{") depth++;
          else if (text[j] === "}") depth--;
          else if (text[j] === "|" && depth === 1) hasPipeAtLevel1 = true;
          j++;
        }

        if (depth === 0 && hasPipeAtLevel1) {
          if (i > lastIndex) {
            const textBefore = text.substring(lastIndex, i);
            parts.push(...parseTextSegment(textBefore));
          }

          const rawMatch = text.substring(i, j);
          const content = rawMatch.substring(1, rawMatch.length - 1);
          const options = splitByPipeOutsideVars(content)
            .map(o => o.trim())
            .filter(Boolean);

          parts.push({
            type: "spintax",
            options: options,
            raw: rawMatch
          });

          lastIndex = j;
          i = j;
          continue;
        }
      }
      i++;
    }

    if (lastIndex < text.length) {
      parts.push(...parseTextSegment(text.substring(lastIndex)));
    }

    return parts;
  };

  const handleWorkingDaysChipClick = (e, index, part) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const dataName = part.value || "";
    const numMatch = dataName.match(/^(\d+) (working days|days) from today$/i);
    const currentCount = numMatch ? parseInt(numMatch[1], 10) : 1;
    setWorkingDaysCount(currentCount);
    setWorkingDaysType(dataName.includes("working days") ? "working" : "calendar");
    setWorkingDaysPopoverPosition({ top: rect.bottom + 6, left: rect.left });
    setWorkingDaysEditingIndex(index);
    setShowWorkingDaysPopover(true);
  };

  const handleWorkingDaysApply = () => {
    if (workingDaysEditingIndex === null) return;
    const count = Math.max(1, Math.floor(Number(workingDaysCount) || 1));
    const tokenName = workingDaysType === "working"
      ? `${count} working days from today`
      : `${count} days from today`;
    const newParts = [...renderedParts];
    newParts[workingDaysEditingIndex] = {
      ...newParts[workingDaysEditingIndex],
      value: tokenName,
      raw: `{{${tokenName}}}`
    };
    onChange(newParts.map(p => p.type === "text" ? p.value : p.raw).join(""));
    setShowWorkingDaysPopover(false);
    setWorkingDaysEditingIndex(null);
  };

  const renderedParts = parseContent(content);

  const handleChipClick = (e, index, options) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    
    setPopoverPosition({
      top: rect.bottom + 6,
      left: rect.left
    });
    setEditingIndex(index);
    setPopoverData({ options: [...options] });
    setShowPopover(true);
  };

  const handleClosePopover = () => {
    setShowPopover(false);
    setPopoverData(null);
    setEditingIndex(null);
  };

  const handleApply = () => {
    if (editingIndex === null || !popoverData) return;
    
    const newParts = [...renderedParts];
    const newOptions = popoverData.options.filter(o => o.trim().length > 0);
    
    if (newOptions.length < 2) {
      // If less than 2 options, convert back to plain text or first option
      newParts[editingIndex] = {
        type: "text",
        value: newOptions[0] || ""
      };
    } else {
      newParts[editingIndex] = {
        ...newParts[editingIndex],
        options: newOptions,
        raw: `{${newOptions.join("|")}}`
      };
    }

    // Reconstruct the full string
    const newContent = newParts.map(p => (p.type === "text" ? p.value : p.raw)).join("");
    onChange(newContent);
    handleClosePopover();
  };

  const handleRemoveSpintax = () => {
    if (editingIndex === null) return;
    const newParts = [...renderedParts];
    newParts.splice(editingIndex, 1);
    const newContent = newParts.map(p => (p.type === "text" ? p.value : p.raw)).join("");
    onChange(newContent);
    handleClosePopover();
  };

  const handleVariableRemove = (index) => {
    const newParts = [...renderedParts];
    newParts.splice(index, 1);
    const newContent = newParts.map(p => p.type === "text" ? p.value : p.raw).join("");
    onChange(newContent);
  };

  let rawOffset = 0;

  return (
    <span className="spintax-parser-container" style={{ whiteSpace: "pre-wrap", display: "inline" }}>
      {renderedParts.map((part, index) => {
        const partStart = rawOffset;
        rawOffset += part.raw ? part.raw.length : part.value.length;

        if (part.type === "text") {
          return (
            <span
              key={index}
              onClick={onEditAt ? (e) => { e.stopPropagation(); onEditAt(partStart); } : undefined}
            >
              {part.value}
            </span>
          );
        }

        if (part.type === "workingDays") {
          return (
            <span
              key={index}
              className="working-days-chip-blot"
              data-name={part.value}
              onClick={(e) => handleWorkingDaysChipClick(e, index, part)}
            >
              {part.value}
            </span>
          );
        }

        if (part.type === "variable") {
          return (
            <span
              key={index}
              className="variable-chip-blot"
              onClick={onEditAt ? (e) => { e.stopPropagation(); onEditAt(partStart); } : undefined}
            >
              {part.value}
            </span>
          );
        }

        return (
          <span
            key={index}
            className="spintax-chip-blot"
            onClick={(e) => handleChipClick(e, index, part.options)}
            style={{ cursor: "pointer", display: "inline-block", verticalAlign: "middle" }}
          >
            <span className="spintaxChipText">{part.options[0]}</span>
            <span className="spintaxChipCount">
              · {part.options.length}
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: "4px" }}>
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </span>
        );
      })}

      {showWorkingDaysPopover && (
        <>
          <div
            className="chipPopoverOverlay"
            onClick={(e) => { e.stopPropagation(); setShowWorkingDaysPopover(false); }}
          />
          <div
            className="workingDaysChipPopover"
            style={{ top: workingDaysPopoverPosition.top, left: workingDaysPopoverPosition.left, position: "fixed", zIndex: 11000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chipPopoverTitle">
              {workingDaysType === "working" ? "Working days from today" : "Days from today"}
            </div>
            <div className="chipVariantCard">
              <input
                type="number"
                className="workingDaysInput"
                min="1"
                max="365"
                value={workingDaysCount}
                autoFocus
                onChange={e => setWorkingDaysCount(e.target.value)}
              />
            </div>
            <div className="chipPopoverActions">
              <button className="chipPopoverCancel" onClick={() => setShowWorkingDaysPopover(false)}>Cancel</button>
              <button className="chipPopoverApply" onClick={handleWorkingDaysApply}>Apply</button>
            </div>
          </div>
        </>
      )}

      {showPopover && popoverData && (
        <>
          <div
            className="chipPopoverOverlay"
            onClick={(e) => {
              e.stopPropagation();
              handleClosePopover();
            }}
          />
          <div
            className="spintaxChipPopover"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              position: "fixed",
              zIndex: 11000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chipPopoverHeader">
              <div className="chipPopoverTitle">Variants — one is sent randomly</div>
              <button 
                className="chipPopoverDelete" 
                onClick={handleRemoveSpintax}
                title="Remove Spintax"
              >
                Delete
              </button>
            </div>
            {popoverData.options.map((opt, i) => (
              <div key={i} className="chipVariantCard">
                <textarea
                  className="chipVariantInput"
                  value={opt}
                  placeholder={`Variant ${i + 1}`}
                  rows={2}
                  onChange={(e) => {
                    const newOptions = [...popoverData.options];
                    newOptions[i] = e.target.value;
                    setPopoverData({ ...popoverData, options: newOptions });
                  }}
                  autoFocus={i === 0}
                />
                {popoverData.options.length > 2 && (
                  <button
                    className="chipVariantDelete"
                    onClick={() => {
                      const newOptions = popoverData.options.filter((_, j) => j !== i);
                      setPopoverData({ ...popoverData, options: newOptions });
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {popoverData.options.length < 3 && (
              <div
                className="chipPopoverAdd"
                onClick={() =>
                  setPopoverData({
                    ...popoverData,
                    options: [...popoverData.options, ""]
                  })
                }
              >
                + Add variant
              </div>
            )}
            <div className="chipPopoverActions">
              <button className="chipPopoverCancel" onClick={handleClosePopover}>
                Cancel
              </button>
              <button className="chipPopoverApply" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </span>
  );
};

export default SpintaxParser;
